/**
 * Shared date/time formatters for the SPA.
 * Backend returns ISO strings like "2026-05-24T18:30:00.000Z" (date-only DB columns
 * get serialized as midnight UTC). These helpers render them in IST-friendly,
 * human-readable form.
 */

const LOCALE = "en-IN";
const TIME_ZONE = "Asia/Kolkata";

function toValidDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const str = String(value).trim();
  if (!str) return null;
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? null : d;
}

function preferredDateParts() {
  try {
    if (typeof window !== "undefined" && window.localStorage.getItem("nema_date_format") === "DD/MM/YYYY") {
      return { day: "2-digit", month: "2-digit", year: "numeric" };
    }
  } catch {
    /* keep the written month style */
  }
  return { day: "2-digit", month: "short", year: "numeric" };
}

/** "25 May 2026" (date only). Returns fallback for missing/invalid values. */
export function formatDate(value, fallback = "-") {
  const d = toValidDate(value);
  if (!d) return fallback;
  return d.toLocaleDateString(LOCALE, {
    ...preferredDateParts(),
    timeZone: TIME_ZONE,
  });
}

/** "25 May 2026, 10:30 AM" (date + time in IST). */
export function formatDateTime(value, fallback = "-") {
  const d = toValidDate(value);
  if (!d) return fallback;
  return d.toLocaleString(LOCALE, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: TIME_ZONE,
  });
}

/** "10:30 AM" (time only in IST). */
export function formatTime(value, fallback = "-") {
  const d = toValidDate(value);
  if (!d) return fallback;
  return d.toLocaleTimeString(LOCALE, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: TIME_ZONE,
  });
}

/** "Mon, 25 May 2026" (weekday + date). Useful for schedule rows. */
export function formatDateWithWeekday(value, fallback = "-") {
  const d = toValidDate(value);
  if (!d) return fallback;
  return d.toLocaleDateString(LOCALE, {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: TIME_ZONE,
  });
}

/** ISO yyyy-mm-dd in IST (for <input type="date"> defaults). */
export function formatDateInputValue(value) {
  const d = toValidDate(value);
  if (!d) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: TIME_ZONE,
  }).formatToParts(d);
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;
  return y && m && day ? `${y}-${m}-${day}` : "";
}

/**
 * "Now" as wall-clock parts in the app's configured timezone (Asia/Kolkata),
 * independent of the visitor's own browser/OS timezone. Use this — not a
 * browser-local `new Date(y, m, d, hh, mm)` — for any "is this slot in the
 * past" comparison so it matches the backend's IST-based booking logic.
 */
export function getNowInAppTimezone(now = new Date()) {
  const dateParts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const y = dateParts.find((p) => p.type === "year")?.value;
  const m = dateParts.find((p) => p.type === "month")?.value;
  const d = dateParts.find((p) => p.type === "day")?.value;
  const timeParts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const hour = Number(timeParts.find((p) => p.type === "hour")?.value);
  const minute = Number(timeParts.find((p) => p.type === "minute")?.value);
  return {
    dateIso: y && m && d ? `${y}-${m}-${d}` : "",
    minutesSinceMidnight: Number.isFinite(hour) && Number.isFinite(minute) ? hour * 60 + minute : null,
  };
}

/** True when a "yyyy-MM-dd" + "HH:MM" wall-clock slot is already past "now" in app time. */
export function isSlotPastNow(dateIso, hhmm, now = new Date()) {
  const { dateIso: todayIso, minutesSinceMidnight: nowMin } = getNowInAppTimezone(now);
  const d = String(dateIso || "").slice(0, 10);
  if (!d || !todayIso) return false;
  if (d < todayIso) return true;
  if (d > todayIso) return false;
  const match = String(hhmm || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match || nowMin == null) return false;
  const slotMin = Number(match[1]) * 60 + Number(match[2]);
  return slotMin <= nowMin;
}

const formatters = {
  formatDate,
  formatDateTime,
  formatTime,
  formatDateWithWeekday,
  formatDateInputValue,
  getNowInAppTimezone,
  isSlotPastNow,
};

export default formatters;
